import { createContext, useContext } from 'react'
import type { ConfirmTone } from '@/shared/ui/ConfirmDialog'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
}

export interface ConfirmDialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

export const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null)

export const useConfirmDialog = (): ConfirmDialogContextValue => {
  const value = useContext(ConfirmDialogContext)
  if (!value) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider')
  }
  return value
}

export interface ConfirmDeleteOptions {
  /** Nome da entidade (ex.: "paciente", "documento", "local") */
  entityName: string
  /** Label do item quando exclusão é específica (ex.: título do documento) */
  itemLabel?: string
  /** Descrição customizada. Se não informada, usa padrão baseado em itemLabel. */
  description?: string
}

/**
 * Gera opções padronizadas para confirmação de exclusão.
 * Uso: const ok = await confirm(buildConfirmDeleteOptions({ entityName: 'paciente', description: '...' }))
 */
export const buildConfirmDeleteOptions = ({
  entityName,
  itemLabel,
  description,
}: ConfirmDeleteOptions): ConfirmOptions => ({
  title: `Excluir ${entityName}?`,
  description:
    description ??
    (itemLabel ? `"${itemLabel}" será removido permanentemente.` : 'Esta ação não pode ser desfeita.'),
  confirmLabel: 'Excluir',
  cancelLabel: 'Cancelar',
  tone: 'danger',
})
